import {
  ChevronDown,
  ChevronUp,
  Crown,
  ExternalLink,
  Lock,
  Plus,
  Trash2,
  Unlock,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AuthoredIcon } from '@/components/admin/shared/authored-icon'
import { AutoSaveInput } from '@/components/ui/auto-save-input'
import { AutoSaveToggle } from '@/components/ui/auto-save-toggle'
import { Button } from '@/components/ui/button'
import { AUTO_SAVE_DEBOUNCE_MS } from '@/hooks/use-auto-save'
import {
  pickSDDisplayCandidate,
  sdCandidateRowId,
  toSDCandidatesFromRouter,
} from '@/lib/problems/sd-source-candidates'
import { type RouterInputs, type RouterOutputs, trpc } from '@/lib/trpc'
import { toastError, toastSuccess } from '@/lib/utils/toast-helpers'
import { AddSourceModal } from '../problems/add-source-modal'
import type { Source } from '../problems/types'

/**
 * One attribution row, as the router returns it.
 *
 * No `slug`, `difficulty` or `order` since LG-930: the problem owns its
 * identity, and these rows only record where else it is listed. Which row is
 * special is `isOriginal`, an explicit marker — never a position in the list.
 *
 * Derived rather than re-declared: the hand-written shape this replaced was
 * asserted onto the query result, so a router change type-checked clean here
 * and only showed up at runtime.
 */
export type SDProblemSource =
  RouterOutputs['admin']['content']['getSystemDesignProblem']['sources'][number]

/**
 * The columns a source card may save, derived from the mutation's own input so
 * the pair can never drift apart — the same derivation the coding editor's
 * `SavableSourceField` uses.
 */
type SDUpdateSourceInput =
  RouterInputs['admin']['content']['updateSystemDesignProblemSource']
type SavableSDSourceField = Extract<
  keyof Omit<SDUpdateSourceInput, 'problemId' | 'sourceId'>,
  keyof SDProblemSource
>

interface SDSourcesSectionProps {
  problemId: string
  problemTitle: string
  /**
   * `SystemDesignProblem.sourceId` — the source that owns the problem.
   *
   * Required, and not defaulted: passing `null` disables the second rung of
   * `pickSDDisplayCandidate` entirely, so a caller that simply forgot would
   * silently answer a different question than the server does.
   */
  owningSourceId: string | null
  sources: SDProblemSource[]
  onSourcesUpdate: (sources: SDProblemSource[]) => void
}

export function SDSourcesSection({
  problemId,
  problemTitle,
  owningSourceId,
  sources: initialSources,
  onSourcesUpdate,
}: SDSourcesSectionProps) {
  const [sources, setSources] = useState<SDProblemSource[]>(initialSources)
  const [expandedSourceId, setExpandedSourceId] = useState<string | null>(null)
  // Auto-expand the original source once per problem, on first data arrival.
  // This component stays mounted across problem switches, so we key the guard
  // on `problemId`: a bare boolean would fire once ever (skipping later
  // problems) and the previous problem's expanded card would leak into the
  // next. Keying on the id re-arms per problem; not depending on
  // `expandedSourceId` keeps a manual collapse from re-triggering the expand.
  const autoExpandedProblemIdRef = useRef<string | null>(null)
  const [savingSource, setSavingSource] = useState(false)

  // Add source dropdown + modal
  const [addSourceDropdownOpen, setAddSourceDropdownOpen] = useState(false)
  const [addSourceModalOpen, setAddSourceModalOpen] = useState(false)
  const addSourceDropdownRef = useRef<HTMLDivElement>(null)

  const sourcesRef = useRef(sources)
  useEffect(() => {
    sourcesRef.current = sources
  }, [sources])

  // Sync sources from props. The ref is advanced here too: the effect above runs
  // a render later, and the add/delete handlers read the ref, so leaving it
  // behind would let a click right after a prop sync operate on the old rows.
  useEffect(() => {
    sourcesRef.current = initialSources
    setSources(initialSources)
  }, [initialSources])

  // Expand the original source once per problem. Read `initialSources` (the
  // prop), not local `sources`: on a problem switch `problemId` and
  // `initialSources` update together, whereas local `sources` lags a render
  // behind (it's synced from the prop in a separate effect), so keying off it
  // would mark the new problem handled using the previous problem's rows.
  useEffect(() => {
    if (autoExpandedProblemIdRef.current === problemId) return
    if (initialSources.length === 0) return
    autoExpandedProblemIdRef.current = problemId
    // Which card opens is the same question the homework row asks, so it goes
    // through the same rule rather than a third local ladder — that also stops
    // the editor auto-opening a source no reader would ever credit.
    const chosen = pickSDDisplayCandidate(
      toSDCandidatesFromRouter(owningSourceId, initialSources),
    )
    // `sdCandidateRowId`, not `chosen.id`: the owning candidate carries an
    // `owning:` prefix that matches no card in the list.
    setExpandedSourceId(
      chosen ? sdCandidateRowId(chosen) : initialSources[0].id,
    )
  }, [problemId, initialSources, owningSourceId])

  // Fetch available sources via tRPC
  const { data: availableSourcesFromApi, isLoading: loadingAvailableSources } =
    trpc.admin.sources.list.useQuery()

  // tRPC mutations
  const createSourceMutation =
    trpc.admin.content.createSystemDesignProblemSource.useMutation()
  const deleteSourceMutation =
    trpc.admin.content.deleteSystemDesignProblemSource.useMutation()
  const updateSourceMutation =
    trpc.admin.content.updateSystemDesignProblemSource.useMutation()

  // Sources available to add (not already linked)
  const availableSourcesToAdd = useMemo(() => {
    const existingSourceNames = new Set(sources.map((s) => s.source))
    return (availableSourcesFromApi ?? [])
      .filter((s) => !existingSourceNames.has(s.name ?? ''))
      .map((s) => ({
        id: s.id,
        name: s.name ?? 'Unknown Source',
        icon: s.icon,
      }))
  }, [sources, availableSourcesFromApi])

  // The server already returns rows in a stable order (created, then id); this
  // renders them as given rather than re-deriving a rank the rows no longer
  // carry.
  const sortedSources = sources

  // Add source
  const handleAddSource = useCallback(
    async (
      sourceName: string,
      sourceIcon: string | null,
      sourceId?: string,
    ) => {
      // `sourcesRef.current`, not the `sources` render closure: a keystroke in a
      // source card advances the ref immediately, so this reads the current rows
      // even when the callback was created before that edit.
      if (sourcesRef.current.some((s) => s.source === sourceName)) {
        toastError(null, `Source "${sourceName}" already exists`)
        return
      }

      let resolvedSourceId = sourceId
      if (!resolvedSourceId) {
        const sourceRecord = (availableSourcesFromApi ?? []).find(
          (s) => s.name === sourceName,
        )
        if (!sourceRecord) {
          toastError(null, `Source "${sourceName}" not found`)
          return
        }
        resolvedSourceId = sourceRecord.id
      }

      setSavingSource(true)
      try {
        const result = await createSourceMutation.mutateAsync({
          problemId,
          sourceId: resolvedSourceId,
          title: problemTitle,
        })

        const newSources = [...sourcesRef.current, result.source]
        sourcesRef.current = newSources
        setSources(newSources)
        onSourcesUpdate(newSources)
        toastSuccess(`Added ${sourceName} source`)
      } catch (error) {
        toastError(error, 'Failed to add source')
      } finally {
        setSavingSource(false)
      }
    },
    [
      problemId,
      problemTitle,
      availableSourcesFromApi,
      createSourceMutation,
      onSourcesUpdate,
    ],
  )

  // Delete source
  const handleDeleteSource = useCallback(
    async (sourceId: string) => {
      const sourceToDelete = sourcesRef.current.find((s) => s.id === sourceId)
      if (!sourceToDelete) return

      if (sourcesRef.current.length <= 1) {
        toastError(null, 'Cannot delete the last source')
        return
      }

      // eslint-disable-next-line no-alert -- Intentional confirmation dialog
      const confirmed = window.confirm(
        `Delete source "${sourceToDelete.source}"? This action cannot be undone.`,
      )
      if (!confirmed) return

      try {
        await deleteSourceMutation.mutateAsync({
          problemId,
          sourceId,
        })

        const newSources = sourcesRef.current.filter((s) => s.id !== sourceId)
        sourcesRef.current = newSources
        setSources(newSources)
        onSourcesUpdate(newSources)
        toastSuccess(`Deleted ${sourceToDelete.source} source`)
      } catch (error) {
        toastError(error, 'Failed to delete source')
      }
    },
    [problemId, deleteSourceMutation, onSourcesUpdate],
  )

  // Update local source state (on keystroke)
  const handleLocalSourceUpdate = useCallback(
    <K extends SavableSDSourceField>(
      sourceId: string,
      field: K,
      value: SDProblemSource[K],
    ) => {
      // The ref is advanced here rather than left to its effect a render later,
      // so a save landing in the same tick — the toggles call this and then
      // `onSaveSourceField` synchronously — starts from the typed value instead
      // of the pre-edit snapshot, and back-to-back edits compose.
      const next = sourcesRef.current.map((s) =>
        s.id === sourceId ? { ...s, [field]: value } : s,
      )
      sourcesRef.current = next
      setSources(next)
    },
    [],
  )

  // Save source field to API
  const handleSaveSourceField = useCallback(
    async <K extends SavableSDSourceField>(
      sourceId: string,
      field: K,
      value: SDProblemSource[K],
    ) => {
      // Read off the ref, not the render closure: a row deleted while a
      // debounced save was pending has nothing to save.
      if (!sourcesRef.current.some((s) => s.id === sourceId)) return

      // Sends the one field being saved and nothing else. The mutation is a
      // partial patch (every write guarded by `if (input.x !== undefined)`), so
      // echoing the row's other values back bought nothing and cost a lost
      // update: this editor's copy goes stale the moment another admin edits
      // the row. Worse for `isOriginal` — every keystroke on the marked row
      // re-asserted `isOriginal: true`, re-running the server's clear-then-set
      // transaction and silently stealing the mark back after someone moved it.
      const result = await updateSourceMutation.mutateAsync({
        problemId,
        sourceId,
        // Written by key: narrowing a generic key does not narrow the value
        // correlated with it, so naming the fields would need an `as` per field
        // that the checker could never verify.
        [field]: value,
      })
      const savedSource = result.source

      // One field in, one field out, and only onto a row that has not moved:
      // adopting the whole returned row would snap a debounced input back to
      // the database's value, erasing anything typed while the save was in
      // flight. Marking an original also clears whichever row held the mark
      // server-side, so those rows are brought back in line here too, or two
      // cards would show a crown.
      const next = sourcesRef.current.map((s) => {
        if (s.id === sourceId) {
          return Object.is(s[field], value)
            ? { ...s, [field]: savedSource[field] }
            : s
        }
        return field === 'isOriginal' && value === true && s.isOriginal
          ? { ...s, isOriginal: false }
          : s
      })
      sourcesRef.current = next
      setSources(next)
      onSourcesUpdate(next)
    },
    [problemId, updateSourceMutation, onSourcesUpdate],
  )

  // Source created via modal
  const handleSourceCreated = useCallback(
    (newSource: Source) => {
      setAddSourceModalOpen(false)
      handleAddSource(newSource.name, newSource.icon, newSource.id)
    },
    [handleAddSource],
  )

  const handleAddSourceClick = async (
    sourceName: string,
    icon: string | null,
  ) => {
    await handleAddSource(sourceName, icon)
    setAddSourceDropdownOpen(false)
  }

  // Dismiss the dropdown on an outside click or Escape — the house pattern for
  // these hand-rolled menus. Only listens while the menu is open.
  useEffect(() => {
    if (!addSourceDropdownOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (
        addSourceDropdownRef.current &&
        !addSourceDropdownRef.current.contains(e.target as Node)
      ) {
        setAddSourceDropdownOpen(false)
      }
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAddSourceDropdownOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [addSourceDropdownOpen])

  return (
    <div className="mt-8">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <label className="text-foreground text-sm font-medium">Sources</label>
          <p className="text-muted-foreground text-sm">
            Platforms where this problem is also listed. Mark the one it
            originated on.
          </p>
        </div>
        <div className="relative" ref={addSourceDropdownRef}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAddSourceDropdownOpen(!addSourceDropdownOpen)}
            disabled={savingSource || loadingAvailableSources}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add Source
            <ChevronDown className="ml-1 h-4 w-4" />
          </Button>
          {addSourceDropdownOpen && (
            <div className="bg-popover border-border absolute top-full right-0 z-10 mt-1 w-64 rounded-md border py-1 shadow-lg">
              {availableSourcesToAdd.length > 0 && (
                <>
                  {availableSourcesToAdd.map((s) => (
                    <button
                      key={s.name}
                      type="button"
                      onClick={() => handleAddSourceClick(s.name, s.icon)}
                      className="hover:bg-muted flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
                    >
                      <AuthoredIcon icon={s.icon} size="sm" fallbackChip />
                      {s.name}
                    </button>
                  ))}
                  <div className="border-border my-1 border-t" />
                </>
              )}
              <button
                type="button"
                onClick={() => {
                  setAddSourceDropdownOpen(false)
                  setAddSourceModalOpen(true)
                }}
                className="hover:bg-muted text-primary flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium"
              >
                <Plus className="h-4 w-4" />
                Add New Source...
              </button>
            </div>
          )}
        </div>
      </div>

      {sources.length === 0 ? (
        <div className="rounded-lg border border-dashed p-6 text-center">
          <p className="text-muted-foreground text-sm">
            No sources configured. Click "Add Source" to add platforms.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {sortedSources.map((source) => (
            <SDSourceCard
              key={source.id}
              source={source}
              isExpanded={expandedSourceId === source.id}
              onToggleExpand={() =>
                setExpandedSourceId(
                  expandedSourceId === source.id ? null : source.id,
                )
              }
              problemTitle={problemTitle}
              sourcesCount={sources.length}
              onLocalSourceUpdate={handleLocalSourceUpdate}
              onSaveSourceField={handleSaveSourceField}
              onDeleteSource={handleDeleteSource}
            />
          ))}
        </div>
      )}

      <AddSourceModal
        isOpen={addSourceModalOpen}
        onClose={() => setAddSourceModalOpen(false)}
        onSourceCreated={handleSourceCreated}
      />
    </div>
  )
}

// ============================================
// Source Card
// ============================================

interface SDSourceCardProps {
  source: SDProblemSource
  isExpanded: boolean
  onToggleExpand: () => void
  problemTitle: string
  sourcesCount: number
  // Correlated key and value, so a control cannot hand a string to a boolean
  // column and have it caught only by the server's Zod, as a 400 after the
  // optimistic toggle has already flipped.
  onLocalSourceUpdate: <K extends SavableSDSourceField>(
    sourceId: string,
    field: K,
    value: SDProblemSource[K],
  ) => void
  onSaveSourceField: <K extends SavableSDSourceField>(
    sourceId: string,
    field: K,
    value: SDProblemSource[K],
  ) => Promise<void>
  onDeleteSource: (sourceId: string) => Promise<void>
}

function SDSourceCard({
  source,
  isExpanded,
  onToggleExpand,
  problemTitle,
  sourcesCount,
  onLocalSourceUpdate,
  onSaveSourceField,
  onDeleteSource,
}: SDSourceCardProps) {
  return (
    <div
      className={`group overflow-hidden rounded-lg border ${
        source.isOriginal
          ? 'border-primary/50 bg-primary/5'
          : 'hover:bg-muted/30'
      }`}
    >
      {/* Source header */}
      <div className="flex w-full items-center gap-3 px-4 py-3">
        <button
          type="button"
          onClick={onToggleExpand}
          aria-expanded={isExpanded}
          className="flex flex-1 items-center gap-3 text-left"
        >
          <AuthoredIcon
            icon={source.sourceIcon}
            size="lg"
            className="flex-shrink-0"
            fallbackChip
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium">{source.source}</span>
              {source.isOriginal && (
                <Crown
                  className="h-4 w-4 text-yellow-500"
                  aria-label="Original source"
                />
              )}
            </div>
            {source.title && source.title !== problemTitle && (
              <p className="text-muted-foreground truncate text-sm">
                {source.title}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {source.externalId && (
              <span className="text-muted-foreground font-mono text-sm">
                #{source.externalId}
              </span>
            )}
            {source.premiumRequired ? (
              <Lock
                className="h-4 w-4 text-amber-500"
                aria-label="Premium required"
              />
            ) : (
              <Unlock
                className="text-muted-foreground h-4 w-4"
                aria-label="Free"
              />
            )}
          </div>
        </button>
        {/* Siblings of the toggle, not children. A link nested inside a button
            is not exposed as its own control by several screen readers, its
            text is absorbed into the button's accessible name, and keyboard
            order within the button is undefined — `stopPropagation` only ever
            patched the mouse case. The chevron is decorative now that the
            button carries `aria-expanded`. */}
        {source.url && (
          <a
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-foreground"
            aria-label="Open problem URL"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
        {isExpanded ? (
          <ChevronUp className="h-4 w-4" aria-hidden="true" />
        ) : (
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        )}
      </div>

      {/* Expanded edit form */}
      {isExpanded && (
        <div className="border-t px-4 py-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor={`sd-source-title-${source.id}`}
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Title on this source
              </label>
              <AutoSaveInput
                id={`sd-source-title-${source.id}`}
                type="text"
                value={source.title || ''}
                onChange={(newValue) =>
                  onLocalSourceUpdate(source.id, 'title', newValue)
                }
                onSave={async (newValue) => {
                  try {
                    await onSaveSourceField(source.id, 'title', newValue)
                  } catch (error) {
                    toastError(error, 'Failed to save')
                    throw error
                  }
                }}
                debounceMs={AUTO_SAVE_DEBOUNCE_MS.slow}
                placeholder="Problem title"
              />
            </div>
            <div>
              <label
                htmlFor={`sd-source-externalId-${source.id}`}
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                External ID
              </label>
              <AutoSaveInput
                id={`sd-source-externalId-${source.id}`}
                type="text"
                value={source.externalId || ''}
                onChange={(newValue) =>
                  onLocalSourceUpdate(source.id, 'externalId', newValue)
                }
                onSave={async (newValue) => {
                  try {
                    await onSaveSourceField(source.id, 'externalId', newValue)
                  } catch (error) {
                    toastError(error, 'Failed to save')
                    throw error
                  }
                }}
                debounceMs={AUTO_SAVE_DEBOUNCE_MS.slow}
                placeholder="e.g., 1 or CH1-Q3"
                className="font-mono"
              />
            </div>
            <div>
              {/* A plain heading, not a `label`: `AutoSaveToggle` takes no `id`
                  prop and renders none, so `htmlFor` pointed at nothing — the
                  switch was unnamed to a screen reader and the word inert to
                  click. Same shape as the coding editor's source card. */}
              <p className="text-muted-foreground mb-1 block text-sm font-medium">
                Original
              </p>
              <AutoSaveToggle
                aria-label="Original"
                checked={source.isOriginal}
                onSave={async (newValue) => {
                  const previousValue = source.isOriginal
                  onLocalSourceUpdate(source.id, 'isOriginal', newValue)
                  try {
                    await onSaveSourceField(source.id, 'isOriginal', newValue)
                  } catch (error) {
                    // Same rollback as Premium below: without it the toggle's
                    // idle resync clobbers its own error revert with the
                    // still-wrong prop. Only this row is restored — the server
                    // rejected the swap, so no other row's mark moved.
                    onLocalSourceUpdate(source.id, 'isOriginal', previousValue)
                    toastError(error, 'Failed to save')
                    throw error
                  }
                }}
                className="origin-left scale-50"
              />
            </div>
            <div>
              {/* Plain heading for the same reason as "Original" above. */}
              <p className="text-muted-foreground mb-1 block text-sm font-medium">
                Premium
              </p>
              <AutoSaveToggle
                aria-label="Premium"
                checked={source.premiumRequired || false}
                onSave={async (newValue) => {
                  const previousValue = source.premiumRequired || false
                  onLocalSourceUpdate(source.id, 'premiumRequired', newValue)
                  try {
                    await onSaveSourceField(
                      source.id,
                      'premiumRequired',
                      newValue,
                    )
                  } catch (error) {
                    // Roll back the optimistic parent-state update so the
                    // toggle's checked prop matches the persisted value on
                    // failure — otherwise AutoSaveToggle's idle resync clobbers
                    // its own error revert with the still-wrong prop.
                    onLocalSourceUpdate(
                      source.id,
                      'premiumRequired',
                      previousValue,
                    )
                    toastError(error, 'Failed to save')
                    throw error
                  }
                }}
                className="origin-left scale-50"
              />
            </div>
            <div>
              <label
                htmlFor={`sd-source-url-${source.id}`}
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Problem URL
              </label>
              <AutoSaveInput
                id={`sd-source-url-${source.id}`}
                type="url"
                value={source.url || ''}
                onChange={(newValue) =>
                  onLocalSourceUpdate(source.id, 'url', newValue || null)
                }
                onSave={async (newValue) => {
                  try {
                    await onSaveSourceField(source.id, 'url', newValue || null)
                  } catch (error) {
                    toastError(error, 'Failed to save')
                    throw error
                  }
                }}
                debounceMs={AUTO_SAVE_DEBOUNCE_MS.slow}
                placeholder="https://..."
              />
            </div>
            <div>
              <label
                htmlFor={`sd-source-editorial-${source.id}`}
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Editorial URL
              </label>
              <AutoSaveInput
                id={`sd-source-editorial-${source.id}`}
                type="url"
                value={source.editorialUrl || ''}
                onChange={(newValue) =>
                  onLocalSourceUpdate(
                    source.id,
                    'editorialUrl',
                    newValue || null,
                  )
                }
                onSave={async (newValue) => {
                  try {
                    await onSaveSourceField(
                      source.id,
                      'editorialUrl',
                      newValue || null,
                    )
                  } catch (error) {
                    toastError(error, 'Failed to save')
                    throw error
                  }
                }}
                debounceMs={AUTO_SAVE_DEBOUNCE_MS.slow}
                placeholder="https://..."
              />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-start">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onDeleteSource(source.id)}
              disabled={sourcesCount <= 1}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="mr-1 h-4 w-4" />
              Delete Source
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
